import { msg } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import type { BackendTypes } from '@tf/api';
import { getFormHash, getFormStatuses } from '@tf/shared';
import type React from 'react';
import { useEffect, useRef } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { isConnectionStepHidden } from '@/components/application/isConnectionStepHidden';
import { LivenessCheckGuard } from '@/components/application/LivenessCheckGuard';
import { useGetConnectionLabels, useMenu } from '@/components/shared';
import { useStructureEntitiesMap } from '@/components/structure/useStructureEntities';
import { useDefinitions } from '@/core/providers';
import { useOriginTenantSlug } from '@/core/providers/OriginTenantSlugProvider';
import { endClientAreaPaths } from '@/routes/endClientArea/paths';

import {
	ApplicationProvider,
	useApplication,
	useApplicationForms,
	useApplicationMainReview,
} from './ApplicationProvider';

const MenuConfigurator: React.FC = () => {
	const { _ } = useLingui();
	const location = useLocation();
	const navigate = useNavigate();

	const { defs } = useDefinitions();
	const originTenantSlug = useOriginTenantSlug();

	const { setMenuItems } = useMenu();
	const application = useApplication();
	const mainReview = useApplicationMainReview();
	const forms = useApplicationForms();

	const getConnectionLabels = useGetConnectionLabels(application.account.entityKind);

	const mainEntity = mainReview.listEntities.find((e) => e.graphNodeId === mainReview.graphNodeId);
	const connectionFilters = defs.accounts[application.account.entityKind].connection_filters;
	const accountConnections = application.account.listConnections
		.filter((accountConnection) => {
			if (isConnectionStepHidden(connectionFilters, accountConnection.connectionKind, mainEntity?.entityKind)) {
				return false;
			}
			return accountConnection.connectionKind !== mainReview.connectionKind || mainEntity?.listConnections.length;
		})
		.sort((a, b) => {
			const connectionsConfig = defs.accounts[application.account.entityKind].connections;
			if (
				connectionsConfig?.[a.connectionKind]?.order !== undefined &&
				connectionsConfig?.[b.connectionKind]?.order !== undefined
			) {
				// @ts-expect-error TODO: upgrade TS
				return connectionsConfig[a.connectionKind].order - connectionsConfig[b.connectionKind].order;
			}

			return a.connectionKind.localeCompare(b.connectionKind);
		});
	const connectionKinds = accountConnections.map((connection) => connection.connectionKind);
	const entities = useStructureEntitiesMap(connectionKinds);

	const menuSignatureRef = useRef<string | null>(null);

	useEffect(() => {
		const formLinks = forms.map((form) => {
			const formUrl = endClientAreaPaths.applicationForm(application.account.graphNodeId, form.name, getFormHash(form));

			const status = getFormStatuses({
				containers: mainReview.listSegmentReviewContainers,
				segmentIdentities: form.segmentIdentities,
			});

			return {
				url: formUrl,
				status: status.structure as BackendTypes.FieldValidationStatus,
				label: form.label,
				isSelected: location.pathname === formUrl,
				onClick: () => navigate(formUrl),
				type: 'form' as const,
			};
		});

		const connectionLinks = accountConnections.map((connection) => {
			const connectionUrl = endClientAreaPaths.applicationConnection(
				application.account.graphNodeId,
				connection.connectionKind,
			);
			const isFilled = entities[connection.connectionKind].some((entity) =>
				entity.connections.some((connection) => connection.connectedEntities.length > 0),
			);
			return {
				url: connectionUrl,
				label: getConnectionLabels(connection.connectionKind).plural,
				isSelected: location.pathname === connectionUrl,
				onClick: () => navigate(connectionUrl),
				status: (isFilled ? 'OK' : 'PENDING_FILL') as BackendTypes.FieldValidationStatus,
				type: 'connection' as const,
			};
		});

		const applicationSubmitUrl = endClientAreaPaths.applicationSubmit(application.account.graphNodeId);

		const reviewStep = {
			url: applicationSubmitUrl,
			label: defs.customization.last_step?.label ?? _(msg`Review and submit`),
			isSelected: location.pathname === applicationSubmitUrl,
			onClick: () => navigate(applicationSubmitUrl),
			status: 'PENDING_FILL' as BackendTypes.FieldValidationStatus,
		};

		const TENANTS_WITH_APPLICATION_AFTER_FIRST_FORM = ['trusteescookislands', 'trusteescookislands-pre', 'local'];

		const applicationIdx = TENANTS_WITH_APPLICATION_AFTER_FIRST_FORM.includes(originTenantSlug.toLowerCase())
			? accountConnections.findIndex((c) => c.connectionKind === 'APPLICATION')
			: -1;

		const applicationLink = applicationIdx !== -1 ? connectionLinks[applicationIdx] : undefined;

		const nextLinks =
			applicationLink && formLinks.length > 0
				? [
						formLinks[0],
						applicationLink,
						...formLinks.slice(1),
						...connectionLinks.filter((_, i) => i !== applicationIdx),
						reviewStep,
					]
				: [...formLinks, ...connectionLinks, reviewStep];

		const nextSignature = JSON.stringify(
			nextLinks.map((link) => [link.url, link.label, link.status, link.isSelected, 'type' in link ? link.type : null]),
		);

		if (nextSignature === menuSignatureRef.current) {
			return;
		}

		menuSignatureRef.current = nextSignature;

		setMenuItems(nextLinks.map(({ url: _url, ...item }) => item));
	}, [
		_,
		getConnectionLabels,
		application.account.graphNodeId,
		forms,
		mainReview.listSegmentReviewContainers,
		location.pathname,
		navigate,
		setMenuItems,
		defs.customization.last_step?.label,
		originTenantSlug,
		entities,
		accountConnections.map,
		accountConnections.findIndex,
	]);

	return null;
};

export const ApplicationLayout: React.FC = () => {
	return (
		<ApplicationProvider>
			<LivenessCheckGuard>
				<MenuConfigurator />
				{/* TODO: error boundary here around Outlet to keep navigation */}
				<Outlet />
			</LivenessCheckGuard>
		</ApplicationProvider>
	);
};
